/**
 * The Settings vocabulary: one group, one row, one switch. Nothing else.
 *
 * Two rules hold the page together, both learned the hard way.
 *
 * 1. ONE container and ONE heading scale. Settings used to mix `Card` +
 *    `CardTitle` (large, its own font) with these groups (h2, smaller), so
 *    "What Ori knows about you" shouted while "Where you are" murmured, for no
 *    reason a reader could infer. Owner, 2026-09-07: *"they are showing
 *    different looks in different tabs… presenting things smartly but in a
 *    simple fashion."* Everything on Settings is a SettingGroup now.
 *
 * 2. AN EMPTY VALUE IS BLANK, PLUS A PENCIL. It used to read "not set", "not
 *    said", "not said yet" — three phrasings of nothing, repeated down the
 *    page until "Not …" was the loudest pattern on it. Emptiness needs no
 *    word; it needs an invitation, which is what the pencil is.
 *
 * The editing model is the app's own (`components/tasks/inline/InlinePopover`,
 * owner 2026-09-05: "the page is the form"): the value is the trigger, one
 * popover holds one control and its own Save, nothing saves on blur.
 */
import React from 'react'
import { Pencil } from 'lucide-react'
import { InlinePopover } from '../tasks/inline/InlinePopover'

interface RowProps {
  /** What this setting is, in the person's words. */
  label: string
  /**
   * The current value, already formatted for reading ("weekdays", "an hour").
   * Empty, null or undefined means unset — render the pencil, never a phrase.
   */
  value?: React.ReactNode
  /** One line on what the answer changes. Omit when the label says it all. */
  because?: string
  /**
   * True when this is the app's assumption rather than the person's answer.
   * Shown by making the value quiet — the group says what quiet means, once,
   * instead of every row carrying the same caption.
   */
  assumed?: boolean
  /** Reset the draft to the stored value. */
  onOpen: () => void
  /** Persist this one fact; throw to show the message and stay open. */
  onSave: () => Promise<void>
  width?: string
  testId?: string
  /** The control(s) for this fact — the same ones the flow uses. */
  children: React.ReactNode
}

/** Nothing worth showing: empty string, null, undefined. */
function isBlank(v: React.ReactNode): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
}

export function SettingRow({
  label, value, because, assumed = false, onOpen, onSave, width = 'w-80', testId, children,
}: RowProps) {
  const blank = isBlank(value)
  return (
    // Stacked on a phone: side by side, a long value ("9:00 AM-5:00 PM,
    // weekdays") squeezes the label into a two-word-wide column of wrapped
    // text. From sm up there is room for both on one line.
    <div className="border-b border-soft py-3 last:border-b-0 sm:flex sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <div className="text-app-body text-primary">{label}</div>
        {because && <div className="mt-0.5 text-app-micro text-muted">{because}</div>}
      </div>
      <div className="mt-1 sm:mt-0 sm:shrink-0 sm:text-right">
        <InlinePopover
          label={label}
          variant={blank ? 'icon' : 'dotted'}
          width={width}
          align="right"
          testId={testId}
          onOpen={onOpen}
          onSave={onSave}
          trigger={
            blank ? (
              <Pencil className="h-4 w-4" aria-hidden="true" />
            ) : (
              <span className={assumed ? 'text-muted' : 'font-medium text-primary'}>{value}</span>
            )
          }
        >
          {children}
        </InlinePopover>
      </div>
    </div>
  )
}

/**
 * A switch that saves the moment it is flipped.
 *
 * A toggle is its own Save: there is no half-flipped state to abandon, so an
 * explicit Save would be a second click for the same decision.
 */
export function SettingSwitch({
  label, because, checked, onChange, disabled = false, testId,
}: {
  label: string
  because?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  testId?: string
}) {
  return (
    <label
      className={`flex cursor-pointer items-start justify-between gap-4 border-b border-soft py-3 last:border-b-0 ${
        disabled ? 'opacity-50' : ''
      }`}
    >
      <span className="min-w-0">
        <span className="block text-app-body text-primary">{label}</span>
        {because && <span className="mt-0.5 block text-app-micro text-muted">{because}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        onChange={e => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 shrink-0 rounded accent-brand"
      />
    </label>
  )
}

/**
 * A group of settings under one heading — the ONLY section container on
 * Settings, so every part of the page carries the same weight.
 *
 * `note` is where a thing gets said once instead of on every row. The grey-value
 * explanation lives here rather than as an "assumed" caption repeated twenty
 * times down the page (owner rule: repetition kills importance).
 */
export function SettingGroup({
  title, blurb, note, action, tone = 'plain', children,
}: {
  /** A node, not just a string, because a couple of headings carry state. */
  title: React.ReactNode
  blurb?: React.ReactNode
  note?: string
  /** One control on the right of the heading — a refresh, a "sign out all". */
  action?: React.ReactNode
  /**
   * The two places on Settings where the border genuinely carries meaning:
   * account security, and the destructive tools. Everything else is 'plain',
   * because a page where several sections shout is a page with no emphasis.
   */
  tone?: 'plain' | 'good' | 'bad' | 'warn'
  children: React.ReactNode
}) {
  const edge =
    tone === 'good' ? 'border-ok' :
    tone === 'bad' ? 'border-bad' :
    tone === 'warn' ? 'border-warn' :
    'border-soft'
  return (
    <section className={`rounded-2xl border ${edge} bg-surface p-5`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-app-body font-semibold text-primary">{title}</h2>
          {blurb && <p className="mt-1 text-app-micro text-secondary">{blurb}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className="mt-3">{children}</div>
      {note && <p className="mt-3 text-app-micro text-muted">{note}</p>}
    </section>
  )
}

/** The one sentence that explains a quiet value, wherever a group has any. */
export const ASSUMED_NOTE =
  'Anything shown in grey is what I am assuming. Set it and it becomes yours.'
